import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { sendToSentry } from '../../utils/error';
import { URL_COUNTRY_WORK_PROFILE_PHOTO } from '../../constants/urls';

// axios.defaults.baseURL is set at app bootstrap (setupAxios) and differs by
// environment (dev points at a separate wallet-api host, prod is same-origin
// /api) -- a plain relative <img src> would resolve against the current
// page's origin instead, which is wrong in dev. The session cookie still
// authenticates the request the same way it does for other API calls.
//
// Read at call time, not module-load time -- axios.defaults.baseURL isn't
// set until setupAxios() runs at app bootstrap, so a module-level constant
// here would risk capturing it while still undefined, depending on import
// order.
//
// If this renders before that finishes, baseURL is still undefined --
// returning null here (rather than building a broken "undefined/profile/..."
// URL) means the photo is simply not rendered yet instead of firing a
// request doomed to fail, and it self-corrects on the next render once
// baseURL is set.
const photoSrc = () => {
  if (!axios.defaults.baseURL) {
    sendToSentry(
      new Error(
        'WalletCountryWorkProfilePhoto: axios.defaults.baseURL not set when building the photo URL',
      ),
    );
    return null;
  }
  return `${axios.defaults.baseURL}${URL_COUNTRY_WORK_PROFILE_PHOTO}`;
};

// Shows the real portrait fetched from /api/profile/country_work_profile/photo.
// Renders nothing (no fallback image) if it's unavailable, rather than a
// generic placeholder standing in for a photo that isn't actually the
// signed-in user's.
const WalletCountryWorkProfilePhoto = ({ alt, className, resetKey }) => {
  const [photoFailed, setPhotoFailed] = useState(false);

  // Resets whenever resetKey changes (e.g. a caller paginating between
  // documents) so a missing photo on one document doesn't carry over and
  // hide the next document's photo too.
  useEffect(() => {
    setPhotoFailed(false);
  }, [resetKey]);

  const src = photoSrc();

  if (!src || photoFailed) {
    return null;
  }

  return (
    <img
      alt={alt}
      className={className}
      src={src}
      onError={() => {
        // <img>'s onError doesn't expose a status code, so this can't
        // distinguish "no photo on file" (a real 404) from an
        // auth/network failure -- logging it at least lets us tell
        // "something failed" apart from "nothing to show" during rollout.
        sendToSentry(
          new Error(
            'WalletCountryWorkProfilePhoto: failed to load country work profile photo',
          ),
        );
        setPhotoFailed(true);
      }}
    />
  );
};

WalletCountryWorkProfilePhoto.propTypes = {
  alt: PropTypes.string,
  className: PropTypes.string,
  resetKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

WalletCountryWorkProfilePhoto.defaultProps = {
  alt: '',
  className: '',
  resetKey: undefined,
};

export default WalletCountryWorkProfilePhoto;
